import React from "react";
import DeleteIcon from "@mui/icons-material/Delete";
import {
  Button,
  Divider,
  FormControl,
  FormControlLabel,
  FormHelperText,
  FormLabel,
  Grid,
  IconButton,
  MenuItem,
  Radio,
  RadioGroup,
  Typography,
} from "@mui/material";
import { FormattedMessage, useIntl } from "react-intl";

import { CustomTextField } from "../../../components/custom-textfield";
import { RadioYesNo } from "../../../components/radio-button-yes-no";
import { SelectCountryTranslated } from "../../../components/select-country";
import {
  Applicant,
  ApplicantErrors,
  EMPTY_IMMIGRATION_PROBLEM,
  EMPTY_PREVIOUS_LEAVE_APPLICATION,
  ImmigrationProblemType,
} from "../../../types/applicants";
import { EMPTY_COUNTRY, Country } from "../../../types/commons";

interface PagePreviousVisaApplicationsProps {
  applicant: Applicant;
  errors: ApplicantErrors;
  onInputChange: (event: React.ChangeEvent<HTMLInputElement>) => void;
  onPreviousLeaveChange: (event: React.ChangeEvent<HTMLInputElement>) => void;
  onAddPreviousLeave: () => void;
  onDeletePreviousLeave: (index: number) => void;
  onImmigrationProblemChange: (
    event: React.ChangeEvent<HTMLInputElement>,
  ) => void;
  onAddImmigrationProblem: () => void;
  onDeleteImmigrationProblem: (index: number) => void;
  onImmigrationProblemCountryChange: (
    event: any,
    newValue: Country | null,
  ) => void;
}

const IMMIGRATION_PROBLEM_OPTIONS: ImmigrationProblemType[] = [
  "visa_refused",
  "refused_entry",
  "refused_stay",
  "refused_asylum",
  "deported",
  "removed",
  "required_to_leave",
  "excluded",
];

const IMMIGRATION_PROBLEM_LABELS: Record<ImmigrationProblemType, string> = {
  visa_refused: "An application for a visa was refused",
  refused_entry: "I was refused entry at the border",
  refused_stay: "I was refused permission to stay or remain",
  refused_asylum: "I was refused asylum",
  deported: "I was deported",
  removed: "I was removed",
  required_to_leave: "I was required to leave",
  excluded: "I was excluded or banned from entry",
};

const MONTH_OPTIONS = Array.from({ length: 12 }, (_, index) => ({
  value: String(index + 1).padStart(2, "0"),
  date: new Date(2020, index, 1),
}));

export const PagePreviousVisaApplications: React.FC<
  PagePreviousVisaApplicationsProps
> = ({
  applicant,
  errors,
  onInputChange,
  onPreviousLeaveChange,
  onAddPreviousLeave,
  onDeletePreviousLeave,
  onImmigrationProblemChange,
  onAddImmigrationProblem,
  onDeleteImmigrationProblem,
  onImmigrationProblemCountryChange,
}) => {
  const intl = useIntl();
  const currentYear = new Date().getFullYear();
  const yearOptions = Array.from({ length: 100 }, (_, index) =>
    String(currentYear - index),
  );
  const previousLeaveApplications = applicant.previous_leave_applications || [];
  const immigrationProblems = applicant.immigration_problems || [];
  const displayedPreviousLeaveApplications =
    applicant.has_applied_for_leave_to_remain &&
    previousLeaveApplications.length === 0
      ? [{ ...EMPTY_PREVIOUS_LEAVE_APPLICATION }]
      : previousLeaveApplications;
  const displayedImmigrationProblems =
    applicant.has_immigration_problems && immigrationProblems.length === 0
      ? [{ ...EMPTY_IMMIGRATION_PROBLEM }]
      : immigrationProblems;

  return (
    <Grid container spacing={2} padding={2}>
      <Grid item xs={12}>
        <Divider textAlign="left">
          <FormattedMessage
            id="divider_previous_visa_leave_to_remain"
            defaultMessage="Leave to remain in the UK"
          />
        </Divider>
      </Grid>

      <Grid item xs={12}>
        <RadioYesNo
          id="has_applied_for_leave_to_remain"
          error={!!errors.has_applied_for_leave_to_remain}
          helperText={errors.has_applied_for_leave_to_remain}
          value={applicant.has_applied_for_leave_to_remain}
          label="Have you ever applied for leave to remain in the UK?"
          onChange={onInputChange}
          autoFocus
        />
        <Typography component="p" variant="body2" color="text.secondary" sx={{ mt: 1.5, mb: 0 }}>
          <FormattedMessage
            id="has_applied_for_leave_to_remain_explanation"
            defaultMessage="Leave to remain is an application made whilst in the UK asking permission to stay in the UK."
          />
        </Typography>
      </Grid>

      {applicant.has_applied_for_leave_to_remain && (
        <React.Fragment>
          {displayedPreviousLeaveApplications.map((leaveApplication, index) => (
            <React.Fragment key={"previous_leave_" + index}>
              <Grid item xs={12} md={3}>
                <CustomTextField
                  translated={false}
                  name={"prev_leave_" + index + "_date_month"}
                  label={intl.formatMessage({
                    id: "common_month",
                    defaultMessage: "Month",
                  })}
                  error={!!errors.previous_leave_applications?.[index]?.date}
                  helperText={errors.previous_leave_applications?.[index]?.date}
                  value={leaveApplication.date?.split("-")[1] || ""}
                  onChange={onPreviousLeaveChange}
                  select
                  fullWidth
                >
                  <MenuItem value="">-</MenuItem>
                  {MONTH_OPTIONS.map((month) => (
                    <MenuItem key={month.value} value={month.value}>
                      {intl.formatDate(month.date, { month: "long" })}
                    </MenuItem>
                  ))}
                </CustomTextField>
              </Grid>

              <Grid item xs={12} md={3}>
                <CustomTextField
                  translated={false}
                  name={"prev_leave_" + index + "_date_year"}
                  label={intl.formatMessage({
                    id: "common_year",
                    defaultMessage: "Year",
                  })}
                  error={!!errors.previous_leave_applications?.[index]?.date}
                  helperText={errors.previous_leave_applications?.[index]?.date}
                  value={leaveApplication.date?.split("-")[0] || ""}
                  onChange={onPreviousLeaveChange}
                  select
                  fullWidth
                >
                  <MenuItem value="">-</MenuItem>
                  {yearOptions.map((year) => (
                    <MenuItem key={year} value={year}>
                      {year}
                    </MenuItem>
                  ))}
                </CustomTextField>
              </Grid>

              <Grid
                item
                xs={12}
                md={6}
                container
                alignItems="center"
                justifyContent="flex-end"
              >
                {index < previousLeaveApplications.length && (
                  <IconButton
                    aria-label={intl.formatMessage({
                      id: "button_delete_previous_leave_application",
                      defaultMessage: "Delete application",
                    })}
                    size="large"
                    onClick={() => onDeletePreviousLeave(index)}
                  >
                    <DeleteIcon />
                  </IconButton>
                )}
              </Grid>

              <Grid item xs={12}>
                <FormControl
                  error={!!errors.previous_leave_applications?.[index]?.result}
                >
                  <FormLabel id={"prev_leave_" + index + "_result_label"}>
                    <FormattedMessage
                      id="previous_leave_application_result"
                      defaultMessage="What was the result of your application?"
                    />
                  </FormLabel>
                  <RadioGroup
                    row
                    aria-labelledby={"prev_leave_" + index + "_result_label"}
                    name={"prev_leave_" + index + "_result"}
                    value={leaveApplication.result || ""}
                    onChange={onPreviousLeaveChange}
                  >
                    <FormControlLabel
                      value="approved"
                      control={<Radio />}
                      label={
                        <FormattedMessage
                          id="previous_leave_application_result_approved"
                          defaultMessage="Approved"
                        />
                      }
                    />
                    <FormControlLabel
                      value="refused"
                      control={<Radio />}
                      label={
                        <FormattedMessage
                          id="previous_leave_application_result_refused"
                          defaultMessage="Refused"
                        />
                      }
                    />
                  </RadioGroup>
                  <FormHelperText>
                    {errors.previous_leave_applications?.[index]?.result}
                  </FormHelperText>
                </FormControl>
              </Grid>

              {leaveApplication.result === "refused" && (
                <Grid item xs={12}>
                  <CustomTextField
                    translated={false}
                    name={"prev_leave_" + index + "_refusal_reason"}
                    label={intl.formatMessage({
                      id: "previous_leave_application_refusal_reason",
                      defaultMessage: "Reason for refusal",
                    })}
                    error={
                      !!errors.previous_leave_applications?.[index]
                        ?.refusal_reason
                    }
                    helperText={
                      errors.previous_leave_applications?.[index]
                        ?.refusal_reason
                    }
                    value={leaveApplication.refusal_reason || ""}
                    onChange={onPreviousLeaveChange}
                    fullWidth
                    multiline
                    minRows={3}
                  />
                </Grid>
              )}
            </React.Fragment>
          ))}

          <Grid item xs={12}>
            <Button variant="outlined" onClick={onAddPreviousLeave}>
              <FormattedMessage
                id="button_add_previous_leave_application"
                defaultMessage="Add additional case"
              />
            </Button>
          </Grid>
        </React.Fragment>
      )}

      <Grid item xs={12}>
        <Divider textAlign="left">
          <FormattedMessage
            id="divider_immigration_problems"
            defaultMessage="Immigration problems"
          />
        </Divider>
      </Grid>

      <Grid item xs={12}>
        <RadioYesNo
          id="has_immigration_problems"
          error={!!errors.has_immigration_problems}
          helperText={errors.has_immigration_problems}
          value={applicant.has_immigration_problems}
          label="Have you had any immigration problems?"
          onChange={onInputChange}
        />
      </Grid>

      {applicant.has_immigration_problems && (
        <React.Fragment>
          {displayedImmigrationProblems.map((problem, index) => {
            const detailsLength = (problem.details || "").length;

            return (
              <React.Fragment key={"immigration_problem_" + index}>
                <Grid item xs={11}>
                  <FormControl
                    error={!!errors.immigration_problems?.[index]?.type}
                  >
                    <FormLabel id={"imm_problem_" + index + "_type_label"}>
                      <FormattedMessage
                        id="immigration_problem_type"
                        defaultMessage="Details of immigration problems"
                      />
                    </FormLabel>
                    <RadioGroup
                      aria-labelledby={"imm_problem_" + index + "_type_label"}
                      name={"imm_problem_" + index + "_type"}
                      value={problem.type || ""}
                      onChange={onImmigrationProblemChange}
                    >
                      {IMMIGRATION_PROBLEM_OPTIONS.map((option) => (
                        <FormControlLabel
                          key={option}
                          value={option}
                          control={<Radio />}
                          label={
                            <FormattedMessage
                              id={"immigration_problem_option_" + option}
                              defaultMessage={
                                IMMIGRATION_PROBLEM_LABELS[option]
                              }
                            />
                          }
                        />
                      ))}
                    </RadioGroup>
                    <FormHelperText>
                      {errors.immigration_problems?.[index]?.type}
                    </FormHelperText>
                  </FormControl>
                </Grid>

                <Grid
                  item
                  xs={1}
                  container
                  alignItems="center"
                  justifyContent="center"
                >
                  {index < immigrationProblems.length && (
                    <IconButton
                      aria-label={intl.formatMessage({
                        id: "button_delete_immigration_problem",
                        defaultMessage: "Delete problem",
                      })}
                      size="large"
                      onClick={() => onDeleteImmigrationProblem(index)}
                    >
                      <DeleteIcon />
                    </IconButton>
                  )}
                </Grid>

                <Grid item xs={12} md={6}>
                  <SelectCountryTranslated
                    id={"imm_problem_country_" + index}
                    label={intl.formatMessage({
                      id: "immigration_problem_country",
                      defaultMessage: "Country",
                    })}
                    error={!!errors.immigration_problems?.[index]?.country}
                    helperText={errors.immigration_problems?.[index]?.country}
                    value={problem.country || EMPTY_COUNTRY}
                    onChange={onImmigrationProblemCountryChange}
                  />
                </Grid>

                <Grid item xs={12} md={3}>
                  <CustomTextField
                    translated={false}
                    name={"imm_problem_" + index + "_date_month"}
                    label={intl.formatMessage({
                      id: "common_month",
                      defaultMessage: "Month",
                    })}
                    error={!!errors.immigration_problems?.[index]?.date}
                    helperText={errors.immigration_problems?.[index]?.date}
                    value={problem.date?.split("-")[1] || ""}
                    onChange={onImmigrationProblemChange}
                    select
                    fullWidth
                  >
                    <MenuItem value="">-</MenuItem>
                    {MONTH_OPTIONS.map((month) => (
                      <MenuItem key={month.value} value={month.value}>
                        {intl.formatDate(month.date, { month: "long" })}
                      </MenuItem>
                    ))}
                  </CustomTextField>
                </Grid>

                <Grid item xs={12} md={3}>
                  <CustomTextField
                    translated={false}
                    name={"imm_problem_" + index + "_date_year"}
                    label={intl.formatMessage({
                      id: "common_year",
                      defaultMessage: "Year",
                    })}
                    error={!!errors.immigration_problems?.[index]?.date}
                    helperText={errors.immigration_problems?.[index]?.date}
                    value={problem.date?.split("-")[0] || ""}
                    onChange={onImmigrationProblemChange}
                    select
                    fullWidth
                  >
                    <MenuItem value="">-</MenuItem>
                    {yearOptions.map((year) => (
                      <MenuItem key={year} value={year}>
                        {year}
                      </MenuItem>
                    ))}
                  </CustomTextField>
                </Grid>

                <Grid item xs={12} md={12}>
                  <CustomTextField
                    translated={false}
                    name={"imm_problem_" + index + "_details"}
                    label={intl.formatMessage({
                      id: "immigration_problem_details",
                      defaultMessage: "More details of what happened",
                    })}
                    error={!!errors.immigration_problems?.[index]?.details}
                    helperText={
                      errors.immigration_problems?.[index]?.details || (
                        <FormattedMessage
                          id="immigration_problem_details_character_count"
                          defaultMessage="{count}/500 characters"
                          values={{ count: detailsLength }}
                        />
                      )
                    }
                    value={problem.details || ""}
                    onChange={onImmigrationProblemChange}
                    fullWidth
                    multiline
                    minRows={4}
                    inputProps={{ maxLength: 500 }}
                  />
                </Grid>
              </React.Fragment>
            );
          })}

          <Grid item xs={12}>
            <Button variant="outlined" onClick={onAddImmigrationProblem}>
              <FormattedMessage
                id="button_add_immigration_problem"
                defaultMessage="Add additional case"
              />
            </Button>
          </Grid>
        </React.Fragment>
      )}
    </Grid>
  );
};
